import { useMemo } from "react";
import { AlertCircle, CheckCircle2, Clock, GitBranch, Users } from "lucide-react";
import { useIsAdmin } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSystems, useVerifySystem } from "@/lib/useSystems";
import type { System, SystemContributor, SystemRepo, SystemStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

// Human labels for the taxonomy group keys stored on each system. Unknown keys
// fall back to a prettified slug so a new category renders sensibly before it's
// added here.
// The full set of 15 taxonomy groups (docs/M1_PLAN.md). Only ~7 have seeded
// systems today, but listing all of them keeps labels polished as the rest
// fill in.
const CATEGORY_LABELS: Record<string, string> = {
  "application-apis": "Application & APIs",
  "data": "Data",
  "identity-access": "Identity & access",
  "platform-infrastructure": "Platform & infrastructure",
  "traffic-scale-performance": "Traffic, scale & performance",
  "reliability-operations": "Reliability & operations",
  "observability": "Observability",
  "security-governance": "Security & governance",
  "engineering-craft": "Engineering craft",
  "languages-dependencies": "Languages & dependencies",
  "testing-quality": "Testing & quality",
  "delivery": "Delivery",
  "cost-resource-governance": "Cost & resource governance",
  "product-experience": "Product & experience",
  "ai-data-integrations": "AI, data & integrations",
};

function prettify(slug: string): string {
  return slug.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function categoryLabel(key: string | null): string {
  if (!key) return "Uncategorized";
  return CATEGORY_LABELS[key] ?? prettify(key);
}

// Status pill styling. Restrained per brand — "current" gets the blue accent;
// the transitional/retired states stay muted so the eye lands on what's live.
const STATUS_STYLES: Record<SystemStatus, string> = {
  current: "bg-bw-blue/10 text-bw-navy",
  evaluating: "bg-bw-blue-muted/20 text-bw-ink-muted",
  deprecated: "bg-muted text-bw-ink-muted line-through",
};

function freshness(lastVerified: string | null): { label: string; stale: boolean } {
  if (!lastVerified) return { label: "Not yet verified", stale: true };
  const timeMs = new Date(lastVerified).getTime();
  // Guard an unparseable timestamp: getTime() returns NaN, which would render
  // "Verified NaNd ago" and (since NaN > 90 is false) wrongly read as fresh.
  if (Number.isNaN(timeMs)) return { label: "Verification date unknown", stale: true };
  const days = Math.floor((Date.now() - timeMs) / 86_400_000);
  // 90 days is the soft freshness window — past it, nudge for a re-verify.
  if (days <= 0) return { label: "Verified today", stale: false };
  if (days === 1) return { label: "Verified yesterday", stale: false };
  return { label: `Verified ${days}d ago`, stale: days > 90 };
}

// How many repos / people a card names before collapsing the rest into a count.
// Azure is used by 30-odd repos; listing them all would bury the purpose line
// the card exists for.
const REPOS_SHOWN = 4;
const PEOPLE_SHOWN = 3;

/** Why we believe a repo uses this system, in words, for the pill's tooltip. */
function sourceHint(repo: SystemRepo): string {
  if (repo.source === "survey") return "read from the repo during the 2026-08-18 survey";
  if (repo.source === "survey-text") return "named in a survey finding from this repo";
  return repo.confidence === "low"
    ? "inferred from the repo summary — low confidence"
    : "inferred from the repo summary";
}

/** Repos the survey actually read are stated plainly; inferred ones read softer. */
function RepoPills({ repos }: { repos: SystemRepo[] }) {
  const shown = repos.slice(0, REPOS_SHOWN);
  const rest = repos.length - shown.length;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
        <GitBranch className="h-3.5 w-3.5" aria-hidden />
        {repos.length} {repos.length === 1 ? "codebase" : "codebases"}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((repo) => (
          <span
            key={repo.name}
            title={`${repo.name} — ${sourceHint(repo)}`}
            className={cn(
              "rounded-pill px-2.5 py-0.5 font-mono text-xs",
              repo.source === "inferred"
                ? "border border-dashed border-bw-blue-muted/50 text-bw-ink-muted"
                : "bg-muted text-bw-navy",
            )}
          >
            {repo.name}
          </span>
        ))}
        {rest > 0 && (
          <span
            className="rounded-pill px-2 py-0.5 font-sans text-xs text-bw-ink-muted"
            title={repos.map((r) => r.name).join(", ")}
          >
            +{rest} more
          </span>
        )}
      </div>
    </div>
  );
}

/** People who have committed to those repos. Agent/CI accounts are excluded. */
function ContributorLine({ people }: { people: SystemContributor[] }) {
  const humans = people.filter((p) => p.kind === "human");
  if (humans.length === 0) return null;
  const shown = humans.slice(0, PEOPLE_SHOWN);
  const rest = humans.length - shown.length;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
        <Users className="h-3.5 w-3.5" aria-hidden />
        Who touches it
      </div>
      <p
        className="font-sans text-sm text-bw-ink"
        title={humans.map((p) => `${p.name} (${p.repo_count} repos)`).join("\n")}
      >
        {shown.map((p) => p.name).join(" · ")}
        {rest > 0 && <span className="text-bw-ink-muted"> · +{rest}</span>}
      </p>
    </div>
  );
}

function groupByCategory(systems: System[]): [string, System[]][] {
  const groups = new Map<string, System[]>();
  for (const s of systems) {
    const key = s.category ?? "";
    const group = groups.get(key) ?? [];
    group.push(s);
    groups.set(key, group);
  }
  // Sort groups by their display label so the page order is stable + readable.
  return [...groups.entries()].sort((a, b) =>
    categoryLabel(a[0]).localeCompare(categoryLabel(b[0])),
  );
}

function SystemCard({ system }: { system: System }) {
  const fresh = freshness(system.last_verified);
  const isAdmin = useIsAdmin();
  const verify = useVerifySystem();
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle>{system.name}</CardTitle>
          <span
            className={cn(
              "shrink-0 rounded-pill px-3 py-1 font-sans text-xs font-semibold capitalize",
              STATUS_STYLES[system.status],
            )}
          >
            {system.status}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 pt-0">
        {system.purpose && (
          <p className="font-sans text-sm text-bw-ink-muted">{system.purpose}</p>
        )}
        {system.decision_points.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {system.decision_points.map((dp) => (
              <span
                key={dp}
                className="rounded-pill bg-bw-blue-muted/15 px-2.5 py-0.5 font-sans text-xs text-bw-navy"
              >
                {prettify(dp)}
              </span>
            ))}
          </div>
        )}
        {system.repos.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-bw-blue-muted/20 pt-3">
            <RepoPills repos={system.repos} />
            <ContributorLine people={system.contributors} />
          </div>
        )}
        {/* A system with no repo isn't a gap in the scan — plenty run at the
            subscription level with no codebase of their own — so say which.
            Only when attribution actually loaded: unread tables produce the
            same empty list, and this sentence would then be a confident lie on
            every card at once. */}
        {system.attribution_available && system.repos.length === 0 && (
          <p className="border-t border-bw-blue-muted/20 pt-3 font-sans text-xs text-bw-ink-muted">
            No codebase references it — configured outside our repos.
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pt-2 font-sans text-xs text-bw-ink-muted">
          <span>{system.owner ?? "Unassigned owner"}</span>
          <span className="flex items-center gap-2">
            <span
              className={cn("flex items-center gap-1", fresh.stale && "text-bw-blue")}
              title={
                system.last_verified
                  ? new Date(system.last_verified).toLocaleString()
                  : undefined
              }
            >
              {fresh.stale ? (
                <Clock className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              )}
              {fresh.label}
            </span>
            {/* The only thing that can advance LAST_VERIFIED. Seeds leave it
                NULL on purpose — a seeded row was asserted by a survey, not
                confirmed by a person — so without this control every row read
                "Not yet verified" forever. Admin-only, mirroring the API. */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                aria-label={`Verify ${system.name}`}
                title="Confirm this entry is still accurate — stamps today's date"
                disabled={verify.isPending}
                onClick={() => verify.mutate(system.system_id)}
              >
                {verify.isPending
                  ? "Verifying…"
                  : system.last_verified
                    ? "Re-verify"
                    : "Verify"}
              </Button>
            )}
          </span>
          {/* Failure has to be visible: the freshness label doesn't move on a
              rejected write, which on its own looks like a click that missed. */}
          {verify.isError && (
            <span role="alert" className="w-full text-right text-bw-danger">
              Couldn't verify — try again.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Systems — the Knowledge layer. Browse the descriptive facts about what we run,
 * grouped by taxonomy category. These rows seed the "current stack" markers in
 * the surveys and are served to LLMs via MCP.
 */
export function SystemsPage() {
  const { data, isLoading, isError, error } = useSystems();

  // Grouping + sorting is cheap for this list, but memoizing keeps it off the
  // path of unrelated re-renders.
  const groupedSystems = useMemo(() => (data ? groupByCategory(data) : []), [data]);

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Current Systems</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          The systems and tools we run — what each is for, which codebases use it,
          who has committed to them, and when it was last verified. This is the
          factual layer that grounds North Star's opinions and the surveys.
        </p>
        <p className="max-w-2xl font-sans text-sm text-bw-ink-muted">
          Codebase and contributor attribution is a point-in-time scan, not a live
          feed. Solid pills were read out of a repository; dashed ones were
          inferred from its summary and are weaker — hover any pill for its
          source.
        </p>
        <p className="max-w-2xl font-sans text-sm text-bw-ink-muted">
          "Verified" means a person confirmed an entry is still accurate. Seeded
          rows start out unverified — they were asserted by a survey, not
          confirmed by anyone — so "Not yet verified" is the honest default
          rather than missing data. Verification goes stale after 90 days.
        </p>
      </section>

      {isLoading && (
        <p className="font-sans text-sm text-bw-ink-muted">Loading systems…</p>
      )}

      {isError && (
        <div className="flex items-center gap-2 rounded-card border border-bw-blue-muted/40 p-4 font-sans text-sm text-bw-ink-muted">
          <AlertCircle className="h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          {Number(error?.status) === 503
            ? "The Knowledge store isn't configured in this environment yet."
            : "Couldn't load systems. Try again shortly."}
        </div>
      )}

      {data && data.length === 0 && (
        <p className="font-sans text-sm text-bw-ink-muted">No systems recorded yet.</p>
      )}

      {groupedSystems.map(([key, systems]) => (
          <section key={key} className="flex flex-col gap-4">
            <h2 className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-ink-muted">
              {categoryLabel(key || null)}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {systems.map((s) => (
                <SystemCard key={s.system_id} system={s} />
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}
